/**
 * Searchable multi-select over TikTok's targetable region list.
 *
 * The options are whatever the parent fetched from /tiktok/regions for the
 * ad group's objective + placements (every placement permits a different
 * country set), or the built-in fallback when TikTok's list comes back empty.
 * TikTok targets numeric location_ids, so the value is the id list; names are
 * looked up from the region rows for the chips.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Check, ChevronsUpDown, Globe, Loader2, MapPin, X } from 'lucide-react';

export interface TiktokRegion { locationId: string; name: string; regionCode?: string; level?: string }

const MAX_VISIBLE = 80;

export function TiktokLocationPicker({
  regions, loading, value, onChange, disabled, placeholder = 'Search a country, city or code…',
}: {
  regions: TiktokRegion[];
  loading?: boolean;
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    inputRef.current?.focus();
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = useMemo(() => new Set(value), [value]);
  const byId = useMemo(() => new Map(regions.map((r) => [r.locationId, r])), [regions]);

  // Match on name OR ISO-style code, so "AE" finds the United Arab Emirates.
  const { countries, others } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (r: TiktokRegion) =>
      !q || r.name.toLowerCase().includes(q) || (r.regionCode ?? '').toLowerCase().includes(q);
    const c: TiktokRegion[] = []; const o: TiktokRegion[] = [];
    for (const r of regions) {
      if (!match(r)) continue;
      if (!r.level || r.level.toUpperCase() === 'COUNTRY') c.push(r); else o.push(r);
    }
    c.sort((a, b) => a.name.localeCompare(b.name));
    o.sort((a, b) => a.name.localeCompare(b.name));
    return { countries: c.slice(0, MAX_VISIBLE), others: o.slice(0, MAX_VISIBLE) };
  }, [regions, query]);

  const toggle = (id: string) => {
    if (disabled) return;
    onChange(selected.has(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  const Row = ({ r, city }: { r: TiktokRegion; city?: boolean }) => {
    const on = selected.has(r.locationId);
    return (
      <button
        type="button"
        onClick={() => toggle(r.locationId)}
        className={clsx(
          'flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs transition-colors',
          on ? 'bg-primary-soft text-text-primary' : 'text-text-primary hover:bg-surface-alt',
        )}
      >
        <Check size={13} className={clsx('shrink-0 text-primary', on ? 'opacity-100' : 'opacity-0')} />
        {city && <MapPin size={11} className="shrink-0 text-text-muted" />}
        <span className="min-w-0 flex-1 truncate">{r.name}</span>
        {city
          ? <span className="text-[10px] text-text-muted">{(r.level ?? 'region').toLowerCase()}</span>
          : r.regionCode && <span className="font-mono text-[11px] text-text-muted">{r.regionCode}</span>}
      </button>
    );
  };

  return (
    <div ref={rootRef} className="relative space-y-2">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          'flex min-h-10 w-full items-center justify-between gap-2 rounded-xl border border-border bg-surface px-3 py-2 text-left text-sm',
          'hover:border-border-strong focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed',
        )}
      >
        <span className="flex items-center gap-2 text-text-muted">
          <Globe size={14} />
          {value.length === 0
            ? (loading ? 'Loading TikTok\'s region list…' : placeholder)
            : `${value.length} location${value.length === 1 ? '' : 's'} selected — add more`}
        </span>
        {loading ? <Loader2 size={14} className="animate-spin text-text-muted" /> : <ChevronsUpDown size={14} className="text-text-muted" />}
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-40 mt-1 overflow-hidden rounded-xl border border-border bg-surface-elevated shadow-elevated">
          <div className="border-b border-border p-2">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type a country, city or code (e.g. UAE, Dubai, AE)"
              className="w-full rounded-lg border border-border bg-surface px-3 py-1.5 text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div className="max-h-72 overflow-y-auto p-1.5">
            {countries.length === 0 && others.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-text-muted">
                {loading ? 'Loading…' : 'No TikTok-targetable location matches.'}
              </div>
            )}
            {countries.length > 0 && (
              <div>
                <div className="px-2.5 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-text-muted">Countries</div>
                {countries.map((r) => <Row key={r.locationId} r={r} />)}
              </div>
            )}
            {others.length > 0 && (
              <div className="mt-1">
                <div className="px-2.5 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-text-muted">Regions &amp; cities</div>
                {others.map((r) => <Row key={r.locationId} r={r} city />)}
              </div>
            )}
          </div>
        </div>
      )}

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((id) => {
            const r = byId.get(id);
            const isCountry = !r?.level || r.level.toUpperCase() === 'COUNTRY';
            return (
              <span key={id} className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-alt py-1 pl-2 pr-1 text-xs text-text-primary">
                {isCountry ? <Globe size={11} className="text-text-muted" /> : <MapPin size={11} className="text-text-muted" />}
                {r?.name ?? id}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => toggle(id)}
                    className="ml-0.5 rounded-full p-0.5 text-text-muted hover:bg-danger-soft hover:text-danger"
                    aria-label={`Remove ${r?.name ?? id}`}
                  >
                    <X size={11} />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
